/**
 * In-memory mock backend for the WebAuthn flow.
 *
 * Active only when config.WEBAUTHN_MOCK is true. Lets the entire flow be
 * exercised in a browser before the real backend exists. Delete-safe: nothing
 * here is imported when WEBAUTHN_MOCK is false.
 *
 * State is module-level and resets on full page reload.
 */
import { NEXT_STEP } from "../config";

const now = () => new Date().toISOString();
const rid = () => "mock_" + Math.random().toString(36).slice(2, 10);

// RP ID must equal the current host for a real local ceremony (localhost is a
// secure context; IPs/domains must match the origin).
const rpId = () => (typeof window !== "undefined" ? window.location.hostname : "localhost");

// base64url encode a string (no padding) — used for user.id.
function b64u(str) {
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// A fresh base64url challenge. Not verified by the mock, but must be valid
// base64url so @simplewebauthn/browser can decode it for the real ceremony.
function randChallenge() {
  const bytes = new Uint8Array(32);
  if (window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  }
  let bin = "";
  bytes.forEach((b) => {
    bin += String.fromCharCode(b);
  });
  return b64u(bin);
}

// Simulated durable stores.
const store = {
  credentials: [], // completed enrollments -> enables assertion path
  request: null, // current device_registration_request
  statusPolls: 0, // used to simulate admin approval latency
  preauthToken: null,
};

// The token payload the app's finalizeWebauthnLogin expects (mirrors the inner
// `response_data` of loginAnalyst/v2/). Values are obviously fake.
function tokenPayload() {
  return {
    token: "mock-token-" + rid(),
    refresh_token: "mock-refresh-" + rid(),
    analyst_id: "mock-analyst-1",
    analyst_name: "Mock Analyst",
    is_admin: true,
    role: { name: "analyst" },
    tabs_accessible: ["home", "analyse", "management"],
  };
}

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

export const mockApi = {
  async login(/* email, password */) {
    await delay(300);
    store.preauthToken = "mock-preauth-" + rid();
    const hasCredential = store.credentials.some((c) => c.status === "active");
    if (hasCredential) {
      return { authenticated: false, next_step: NEXT_STEP.WEBAUTHN_AUTH, preauth_ticket: store.preauthToken };
    }
    // No credential yet — either a fresh request is pending/approved, or none.
    if (store.request && store.request.status === "pending") {
      return { authenticated: false, next_step: NEXT_STEP.REGISTRATION_PENDING, preauth_ticket: store.preauthToken };
    }
    if (store.request && store.request.status === "approved") {
      return {
        authenticated: false,
        next_step: NEXT_STEP.REGISTRATION_READY,
        preauth_ticket: store.preauthToken,
        request_id: store.request._id,
      };
    }
    return { authenticated: false, next_step: NEXT_STEP.REGISTRATION_REQUIRED, preauth_ticket: store.preauthToken };
  },

  async authOptions() {
    await delay(150);
    return {
      challenge: randChallenge(),
      timeout: 120000,
      rpId: rpId(),
      // Real credential IDs captured at registration so navigator.credentials.get
      // can find the on-device passkey (Touch ID fetch).
      allowCredentials: store.credentials
        .filter((c) => c.status === "active")
        .map((c) => ({ id: c.id, type: "public-key", transports: c.transports })),
      userVerification: "preferred",
    };
  },

  async authVerify() {
    await delay(250);
    const active = store.credentials.find((c) => c.status === "active");
    if (active) active.last_used_at = now();
    return tokenPayload();
  },

  async registerRequest(label) {
    await delay(250);
    store.request = {
      _id: rid(),
      status: "pending",
      label: label || "This browser",
      user_agent: navigator.userAgent,
      requested_at: now(),
    };
    store.statusPolls = 0;
    return { status: "pending", request_id: store.request._id };
  },

  async registerStatus() {
    await delay(150);
    if (!store.request) return { status: "none" };
    // Simulate an admin approving after the second poll.
    if (store.request.status === "pending") {
      store.statusPolls += 1;
      if (store.statusPolls >= 2) store.request.status = "approved";
    }
    return {
      status: store.request.status,
      request_id: store.request._id,
      reject_reason: store.request.reject_reason,
    };
  },

  async registerOptions(requestId) {
    await delay(150);
    return {
      rp: { id: rpId(), name: "Analyst Portal" },
      user: { id: b64u("mock-analyst-1"), name: "mock@stepout", displayName: "Mock Analyst" },
      challenge: randChallenge(),
      pubKeyCredParams: [
        { alg: -7, type: "public-key" },
        { alg: -257, type: "public-key" },
      ],
      excludeCredentials: store.credentials.map((c) => ({ id: c.id, type: "public-key" })),
      // Force the platform authenticator so Touch ID / Secure Enclave is used.
      authenticatorSelection: {
        authenticatorAttachment: "platform",
        residentKey: "preferred",
        userVerification: "preferred",
      },
      timeout: 120000,
      attestation: "none",
      _requestId: requestId,
    };
  },

  async registerVerify(requestId, attestation, label) {
    await delay(300);
    if (store.request && store.request._id === requestId) store.request.status = "completed";
    // Real ceremony returns a RegistrationResponseJSON whose `.id` is the true
    // credential ID — store it so the assertion path can locate the passkey.
    const credentialId = attestation && attestation.id ? attestation.id : "mock-cred-" + rid();
    const transports =
      attestation && attestation.response && attestation.response.transports;
    store.credentials.push({
      id: credentialId,
      label: label || store.request?.label || "This browser",
      transports: transports || ["internal"],
      created_at: now(),
      last_used_at: null,
      status: "active",
      fingerprint: credentialId.slice(-6),
    });
    // Spec §20.7: may grant session at completion. We grant to keep the demo flowing.
    return tokenPayload();
  },

  async listCredentials() {
    await delay(150);
    return store.credentials.filter((c) => c.status !== "deleted");
  },

  async renameCredential(id, label) {
    await delay(150);
    const c = store.credentials.find((x) => x.id === id);
    if (c) c.label = label;
    return c;
  },

  async removeCredential(id) {
    await delay(150);
    const c = store.credentials.find((x) => x.id === id);
    if (c) {
      c.status = "deleted";
      c.deleted_at = now();
    }
    return undefined;
  },

  // ---- admin ----
  async adminRequests() {
    await delay(150);
    return { results: store.request ? [store.request] : [], count: store.request ? 1 : 0 };
  },
  async adminApprove(id) {
    await delay(150);
    if (store.request && store.request._id === id) store.request.status = "approved";
    return store.request;
  },
  async adminReject(id, reason) {
    await delay(150);
    if (store.request && store.request._id === id) {
      store.request.status = "rejected";
      store.request.reject_reason = reason;
    }
    return store.request;
  },
  async adminDisableCredential(id) {
    await delay(150);
    const c = store.credentials.find((x) => x.id === id);
    if (c) c.status = "disabled";
    return c;
  },
  async adminDeleteCredential(id) {
    await delay(150);
    const c = store.credentials.find((x) => x.id === id);
    if (c) c.status = "deleted";
    return undefined;
  },
  async adminAudit() {
    await delay(150);
    return { results: [], count: 0 };
  },
};
